import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";
import createDecorator from "final-form-calculate";

import { Button, Typography } from "@material-ui/core";

import InAppNutritionStore from "stores/InAppNutritionStore";
import FoodActiveProgramDictionary from "stores/dictionary/FoodActiveProgramDictionary";

import FormControl, {
  TextFieldControl,
  DateFieldControl,
  CheckboxGroupWithSwitchFieldControl,
  UploadPreviewFieldControl,
  DropdownFieldControl,
  RadioButtonGroupControl,
} from "components/FormControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import Row from "components/Form/Row";
import Loader from "components/Layout/Loader";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";

import {
  compose,
  lengthValidator,
  imageTypesValidator,
  fileSizeValidator,
  startDateDayOfWeekValidator,
  imageIsCorruptedValidator,
} from "utils/validators";

import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import {
  FOOD_PROGRAM_TYPES,
  PUBLISH_STATUSES,
  STRUCTURE_TYPES,
} from "constants/models";

import {
  Container,
  TitleContainer,
  ControlRow,
  Buttons,
  ButtonContainer,
} from "./styles";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  initialValues?: any;
  isFormLoading?: boolean;
};

const MAX_FILE_SIZE_IN_MB = 10;

const FOOD_PROGRAM_TYPE_OPTIONS = [
  { label: "6-Week Program", value: FOOD_PROGRAM_TYPES.SIX_WEEK },
  { label: "VIP & Other", value: FOOD_PROGRAM_TYPES.VIP_AND_OTHER },
];

const structureTypeDecorator = createDecorator({
  field: "structureType",
  updates: {
    foodProgramType: (structureType, allValues, prevValues) => {
      if (
        prevValues?.structureType === STRUCTURE_TYPES.STANDARD &&
        allValues?.structureType === STRUCTURE_TYPES.WEEK
      ) {
        return FOOD_PROGRAM_TYPES.SIX_WEEK;
      }

      if (
        prevValues?.structureType === STRUCTURE_TYPES.WEEK &&
        allValues?.structureType === STRUCTURE_TYPES.STANDARD
      ) {
        return undefined;
      }

      return allValues?.foodProgramType;
    },
    activePrograms: (structureType, allValues, prevValues) => {
      if (
        prevValues?.structureType &&
        allValues?.structureType &&
        prevValues?.structureType !== allValues?.structureType
      ) {
        return [];
      }

      return allValues?.activePrograms;
    },
  },
});

const foodProgramTypeDecorator = createDecorator({
  field: "foodProgramType",
  updates: {
    activePrograms: (foodProgramType, allValues, prevValues) => {
      if (
        prevValues?.foodProgramType &&
        allValues?.foodProgramType &&
        prevValues?.foodProgramType !== allValues?.foodProgramType
      ) {
        return [];
      }

      return allValues?.activePrograms;
    },
    weekNumber: (foodProgramType, allValues, prevValues) => {
      if (
        foodProgramType === undefined ||
        (prevValues?.foodProgramType === FOOD_PROGRAM_TYPES.SIX_WEEK &&
          allValues?.foodProgramType === FOOD_PROGRAM_TYPES.VIP_AND_OTHER)
      ) {
        return undefined;
      }

      return allValues?.weekNumber;
    },
    startDate: (foodProgramType, allValues, prevValues) => {
      if (
        foodProgramType === undefined ||
        (prevValues?.foodProgramType === FOOD_PROGRAM_TYPES.VIP_AND_OTHER &&
          allValues?.foodProgramType === FOOD_PROGRAM_TYPES.SIX_WEEK)
      ) {
        return undefined;
      }

      return allValues?.startDate;
    },
  },
});

@observer
class CategoryForm extends React.Component<Props> {
  @observable updateConfirmationIsOpened: boolean = false;

  get isEditMode() {
    const { initialValues } = this.props;
    return !!initialValues;
  }

  get formTitle() {
    return !this.isEditMode ? "Add Category" : "Edit Category";
  }

  get weekOptions() {
    return [...Array(6)].map((_, index) => ({
      value: index + 1,
      label: `Week ${index + 1}`,
    }));
  }

  get structureTypeOptions() {
    return Object.values(STRUCTURE_TYPES).map(value => ({
      value,
      label: value,
    }));
  }

  @computed get initialValues() {
    return this.isEditMode
      ? InAppNutritionStore.categoryDetails?.formData
      : { structureType: STRUCTURE_TYPES.STANDARD };
  }

  @computed get isDeprecatedCategoryType() {
    return this.isEditMode
      ? InAppNutritionStore.categoryDetails?.isDeprecatedCategoryType
      : false;
  }

  @computed get shouldPrepopulateAccessProgramsDeprecatedCategory() {
    return this.isEditMode
      ? InAppNutritionStore.categoryDetails
          ?.shouldPrepopulateAccessProgramsDeprecatedCategory
      : false;
  }

  @computed get publishStatus() {
    const publishStatus =
      InAppNutritionStore.categoryDetails?.formData?.publishStatus;

    return publishStatus;
  }

  componentDidMount() {
    const { initialValues } = this.props;

    if (initialValues?.id) {
      InAppNutritionStore.fetchCategoryDetails(initialValues.id);
    }
  }

  componentWillUnmount() {
    InAppNutritionStore.resetCategoryDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit } = this.props;

    const shouldShowUpdateConfirmation =
      this.publishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.publishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : onSubmit(values);
  };

  render() {
    const { onClose, isFormLoading, onChangeIsDirty } = this.props;

    return (
      <FormControl
        onSubmit={this.onSubmit}
        initialValues={this.initialValues}
        decorators={[structureTypeDecorator, foodProgramTypeDecorator]}
      >
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <TitleContainer>
            <Typography variant="h1" color="textPrimary">
              {this.formTitle}
            </Typography>
            {this.isEditMode && this.publishStatus && (
              <PublishStatus status={this.publishStatus} />
            )}
          </TitleContainer>

          {!isFormLoading ? (
            <>
              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  label="Title"
                  name="title"
                  placeholder="Enter title"
                  validate={lengthValidator(100)}
                  fullWidth
                  required
                />
              </Row>

              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  name="subTitle"
                  label="Description"
                  placeholder="Enter description"
                  validate={lengthValidator(10000)}
                  minRows={5}
                  maxRows={10}
                  fullWidth
                  multiline
                  required
                />
              </Row>

              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  name="note"
                  label="Note"
                  placeholder="Enter note"
                  validate={lengthValidator(256)}
                  fullWidth
                />
              </Row>

              <Row>
                <UploadPreviewFieldControl
                  name="file"
                  title="Image"
                  acceptedFiles={ACCEPTED_IMAGE_TYPES}
                  validate={compose(
                    this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                    fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                    imageIsCorruptedValidator,
                  )}
                  required
                />
              </Row>

              <FormSpy subscription={{ values: true }}>
                {({ values }) => {
                  const activePrograms = FoodActiveProgramDictionary.getOptionsByFoodProgram(
                    values.foodProgramType,
                  );
                  const showAccessPrograms =
                    this.shouldPrepopulateAccessProgramsDeprecatedCategory ||
                    !this.isDeprecatedCategoryType
                      ? true
                      : values.foodProgramType;

                  return (
                    <>
                      <Row>
                        <RadioButtonGroupControl
                          name="structureType"
                          label="Category Type"
                          options={this.structureTypeOptions}
                          disabled={this.isEditMode}
                          inline
                          required
                        />
                      </Row>

                      {values.structureType === STRUCTURE_TYPES.WEEK && (
                        <Row>
                          <RadioButtonGroupControl
                            name="foodProgramType"
                            label="Program Type"
                            options={FOOD_PROGRAM_TYPE_OPTIONS}
                            inline
                            required
                          />
                        </Row>
                      )}

                      {values.foodProgramType ===
                        FOOD_PROGRAM_TYPES.SIX_WEEK && (
                        <Row>
                          <DropdownFieldControl
                            name="weekNumber"
                            label="Week"
                            options={this.weekOptions}
                            placeholder="Select week"
                            required
                          />
                        </Row>
                      )}
                      {values.foodProgramType ===
                        FOOD_PROGRAM_TYPES.VIP_AND_OTHER && (
                        <Row>
                          <DateFieldControl
                            name="startDate"
                            title="Start date"
                            inputVariant="outlined"
                            margin="none"
                            validate={startDateDayOfWeekValidator(1)}
                            fullWidth
                            required
                          />
                        </Row>
                      )}

                      {showAccessPrograms && (
                        <ControlRow>
                          <CheckboxGroupWithSwitchFieldControl
                            name="activePrograms"
                            title="Access"
                            options={activePrograms}
                            switchLabel="Preview only"
                            inline
                            multi
                            required
                          />
                        </ControlRow>
                      )}
                    </>
                  );
                }}
              </FormSpy>
            </>
          ) : (
            <Row>
              <Loader />
            </Row>
          )}
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      {!this.isEditMode ? "Add" : "Save"}
    </LoadingButton>
  );
}

export default CategoryForm;
